"use client";

import { useMemo } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useGlobalLoader } from "./GlobalLoader";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Página atual (0-indexed) */
  page: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
  /** Opcional: pular pra uma página específica (0-indexed) */
  onPageChange?: (page: number) => void;
  totalPages: number;
}

export function DataTableNoFilter<TData, TValue>({
  columns,
  data,
  onNextPage,
  onPreviousPage,
  onPageChange,
  page,
  totalPages,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const { isLoading } = useGlobalLoader();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Lista compacta de páginas com elipses, no mesmo padrão da página de Logs.
  // Trabalha em 1-indexed pra display, depois converte pra 0-indexed na ação.
  const paginationItems = useMemo<(number | "ellipsis")[]>(() => {
    const items: (number | "ellipsis")[] = [];
    const total = Math.max(1, totalPages);
    const current = page + 1; // 1-indexed pra cálculo
    if (total <= 7) {
      for (let i = 1; i <= total; i++) items.push(i);
    } else {
      items.push(1);
      if (current > 3) items.push("ellipsis");
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) items.push(i);
      if (current < total - 2) items.push("ellipsis");
      items.push(total);
    }
    return items;
  }, [page, totalPages]);

  const irPara = (p1: number) => {
    // p1 é 1-indexed, converte pra 0-indexed
    const target = p1 - 1;
    if (target < 0 || target > Math.max(0, totalPages - 1)) return;
    if (target === page) return;
    if (onPageChange) {
      onPageChange(target);
    } else if (target > page) {
      // fallback usando next/prev (apenas adjacente)
      onNextPage();
    } else {
      onPreviousPage();
    }
  };

  const podeIr = (target: number) =>
    target >= 0 && target <= Math.max(0, totalPages - 1) && target !== page;

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-sm text-muted-foreground"
                >
                  {isLoading ? t("ui.loading") : t("ui.noData")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 rounded-md border p-2">
          <p className="text-xs text-muted-foreground">
            {t("ui.page")}{" "}
            <strong className="text-foreground">{page + 1}</strong> {t("ui.of")}{" "}
            <strong className="text-foreground">{totalPages}</strong>
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!podeIr(0)}
              onClick={() => irPara(1)}
              aria-label={t("ui.firstPage")}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!podeIr(page - 1)}
              onClick={() => irPara(page)}
              aria-label={t("ui.previousPage")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {paginationItems.map((it, i) =>
              it === "ellipsis" ? (
                <span
                  key={`e-${i}`}
                  className="text-xs text-muted-foreground px-1"
                >
                  …
                </span>
              ) : (
                <Button
                  key={it}
                  variant={it === page + 1 ? "default" : "outline"}
                  size="sm"
                  className="h-8 min-w-[2rem] px-2 text-xs"
                  onClick={() => irPara(it)}
                >
                  {it}
                </Button>
              ),
            )}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!podeIr(page + 1)}
              onClick={() => irPara(page + 2)}
              aria-label={t("ui.nextPage")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!podeIr(totalPages - 1)}
              onClick={() => irPara(totalPages)}
              aria-label={t("ui.lastPage")}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
